/**
 * The pieces every wallet card uses — the glyphs, the two one-line states, and
 * the number input the two limit dialogs share.
 *
 * Kept in one place rather than repeated per card because the failure and empty
 * lines are the parts a reader meets when something has gone wrong, and three
 * slightly different renderings of "could not load" is exactly the drift that
 * makes a screenshot ambiguous.
 */
import { useLocale } from "../../i18n/LocaleProvider";

const ICON = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  width: 16,
  height: 16,
};

/** lucide `BadgeCheck` — the subscription card. */
export function BadgeCheckIcon() {
  return (
    <svg {...ICON}>
      <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

/** lucide `Wallet` — the balance. */
export function WalletIcon() {
  return (
    <svg {...ICON}>
      <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" />
      <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
    </svg>
  );
}

/** lucide `Layers` — the plan. */
export function LayersIcon() {
  return (
    <svg {...ICON}>
      <path d="m12 2 9 5-9 5-9-5 9-5Z" />
      <path d="m3 12 9 5 9-5" />
      <path d="m3 17 9 5 9-5" />
    </svg>
  );
}

/** lucide `History` — the purchases ledger. */
export function HistoryIcon() {
  return (
    <svg {...ICON}>
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l4 2" />
    </svg>
  );
}

/** lucide `SlidersHorizontal` — the spend limits. */
export function SlidersIcon() {
  return (
    <svg {...ICON}>
      <path d="M21 4h-7M10 4H3M21 12h-9M8 12H3M21 20h-5M12 20H3" />
      <path d="M14 2v4M8 10v4M16 18v4" />
    </svg>
  );
}

/** lucide `AlertCircle`, at the size the two message rows use it. */
export function AlertCircleIcon() {
  return (
    <svg {...ICON} width={14} height={14}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}

/**
 * A card that could not load.
 *
 * `role="alert"` so it is announced: a wallet figure that silently fails to
 * arrive looks identical to a balance of nothing.
 */
export function ErrorRow({ message }: { message: string }) {
  return (
    <p className="wallet-msg" role="alert">
      <AlertCircleIcon />
      <span>{message}</span>
    </p>
  );
}

/** A card that loaded and has nothing in it — not the same thing as a failure. */
export function EmptyRow({ message }: { message: string }) {
  return <p className="settings-note is-centered">{message}</p>;
}

/**
 * The limit box.
 *
 * `type="number"` with `min={0}`, and the value is held as a **string** by every
 * caller until submit: `null` (inherit) and `0` (may spend nothing) are
 * different settings, and a numeric state would have to pick one of them to
 * represent an empty box.
 */
export function LimitInput({
  value,
  onChange,
  placeholder,
  disabled,
  label,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <input
      className="settings-input limit-input"
      type="number"
      min={0}
      step={1000000}
      inputMode="numeric"
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      aria-label={label}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** One labelled figure in the plan card's grid. */
export function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="wallet-field">
      <div className="wallet-field-label">{label}</div>
      <div className="wallet-field-value">{value}</div>
    </div>
  );
}

/**
 * Dates, in the reader's locale.
 *
 * `timeZone` is passed through where the caller has the plan's: a cycle the
 * server bucketed against `Asia/Taipei` resets on a different calendar day for
 * a reader in another zone, and showing theirs would name the wrong day.
 */
export function useDateFormat(): (iso: string | null, timeZone?: string) => string {
  const { locale } = useLocale();
  return (iso, timeZone) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString(locale === "zh" ? "zh-TW" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        ...(timeZone ? { timeZone } : {}),
      });
    } catch {
      return iso;
    }
  };
}

/** The locale tag the credit formatter groups digits with. */
export function useNumberLocale(): string {
  const { locale } = useLocale();
  return locale === "zh" ? "zh-TW" : "en-US";
}
